import * as React from 'react';
import { INITIAL_CAMERA_STATE, SCANNER_CAMERA_CONSTRAINTS, SCAN_FRAME_INTERVAL_MS, cameraFailureState, } from './camera-state';
import { loadQrDetector } from './qr-decoder';
/**
 * **The camera, its decoder, and the loop between them** — one hook for every scanner
 * (`EVT-042` AC-6).
 *
 * Written for the member app's connect scanner (`MEM-028`) and moved here when the check-in screen
 * needed the same thing. It owns the hardware and nothing else: it opens the camera while `active`,
 * reads a frame every {@link SCAN_FRAME_INTERVAL_MS}, and hands each frame's payloads to `onFrame`
 * — including an empty list, so a caller can tell "no code in view" from "no frame read". What a
 * payload *is*, and what to do with it, is the caller's: the member app filters to its connect
 * token shape, and the door sends what it read to the check-in route.
 *
 * ## One teardown for every way out
 *
 * The effect's cleanup stops the interval, stops every track on the stream and drops the element's
 * source. A React component with `getUserMedia` in it and two places that stop it has a third place
 * that does not — and a phone whose camera light stays on after the scanner closes is the kind of
 * thing that gets an app deleted. `active` going false, an unmount, and a navigation all run it.
 *
 * **A hidden page runs it too, when the caller asks** (`EVT-042` AC-4). Given `onHidden`, the hook
 * stops the stream the moment `document.visibilityState` becomes `hidden` — synchronously, so no
 * interval tick can read a frame between the event and the caller's re-render — and then calls
 * `onHidden` so the caller can mark its scanner closed. It does not reopen on return: the camera
 * never opens without being asked. Without `onHidden` nothing about visibility changes, which is
 * the member scanner's behaviour as it was built.
 *
 * ## Frames are held back, not the camera, while `paused`
 *
 * A caller with a request in flight sets `paused`: frames stop being decoded and handed on, and the
 * camera stays open, so the next person is already in the viewfinder when the answer comes back.
 */
export function useQrCamera({ active, paused, onFrame, onHidden, }) {
    const videoRef = React.useRef(null);
    const [state, setState] = React.useState(INITIAL_CAMERA_STATE);
    // Read inside the loop so a re-render does not restart the camera to pick up a new callback.
    const pausedRef = React.useRef(paused);
    pausedRef.current = paused;
    const onFrameRef = React.useRef(onFrame);
    onFrameRef.current = onFrame;
    const onHiddenRef = React.useRef(onHidden);
    onHiddenRef.current = onHidden;
    React.useEffect(() => {
        if (!active) {
            setState(INITIAL_CAMERA_STATE);
            return;
        }
        let live = true;
        let stream = null;
        let timer = null;
        // Function declarations, because each names the other: `stop` removes the listener, and the
        // listener stops.
        function stop() {
            live = false;
            if (timer !== null)
                clearInterval(timer);
            stream?.getTracks().forEach((track) => {
                track.stop();
            });
            if (videoRef.current !== null)
                videoRef.current.srcObject = null;
            document.removeEventListener('visibilitychange', onVisibilityChange);
        }
        function onVisibilityChange() {
            if (document.visibilityState !== 'hidden')
                return;
            const hidden = onHiddenRef.current;
            if (hidden === undefined)
                return;
            stop();
            hidden();
        }
        document.addEventListener('visibilitychange', onVisibilityChange);
        void (async () => {
            let detect;
            try {
                ({ detect } = await loadQrDetector());
            }
            catch {
                // The decoder chunk or its WebAssembly would not load. The camera would show a picture
                // that could never resolve, so it is not started at all and the field is the way in.
                if (live)
                    setState({ kind: 'failed' });
                return;
            }
            if (!live)
                return;
            // Outside a secure context `mediaDevices` does not exist at all — an `http://` page, or an
            // engine with no camera API. Either way there is no camera this page can use.
            if (typeof navigator === 'undefined' || navigator.mediaDevices === undefined) {
                setState({ kind: 'unavailable' });
                return;
            }
            try {
                stream = await navigator.mediaDevices.getUserMedia(SCANNER_CAMERA_CONSTRAINTS);
            }
            catch (error) {
                if (live)
                    setState(cameraFailureState(error));
                return;
            }
            if (!live || videoRef.current === null) {
                stream.getTracks().forEach((track) => {
                    track.stop();
                });
                return;
            }
            const video = videoRef.current;
            video.srcObject = stream;
            try {
                await video.play();
            }
            catch {
                // Autoplay refused — rare with a muted, `playsInline` element, and not fatal: some engines
                // still deliver frames. The state below is what the person is told if it never does.
            }
            if (!live)
                return;
            setState({ kind: 'live' });
            timer = setInterval(() => {
                if (!live || pausedRef.current)
                    return;
                if (video.readyState < HAVE_CURRENT_DATA || video.videoWidth === 0)
                    return;
                void detect(video)
                    .then((payloads) => {
                    if (!live || pausedRef.current)
                        return;
                    onFrameRef.current(payloads);
                })
                    .catch(() => {
                    // A frame that would not decode is an ordinary frame. The loop keeps looking; a
                    // decoder that is broken rather than unlucky surfaces as never resolving, which the
                    // person answers with the field.
                });
            }, SCAN_FRAME_INTERVAL_MS);
        })();
        return stop;
    }, [active]);
    return { videoRef, state };
}
/** `HTMLMediaElement.HAVE_CURRENT_DATA` — there is a frame to read. */
const HAVE_CURRENT_DATA = 2;
//# sourceMappingURL=use-qr-camera.js.map